import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, Play } from "lucide-react";
import Logo from "@/components/Logo";

const Index = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist relative">
      {/* Faint 48px grid — hardware-console feel */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />

      <div className="relative z-10">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <header className="sticky top-0 z-50 border-b border-console-line bg-console-bg/85 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Logo tone="text" size="md" />
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-console-ok shadow-[0_0_6px_#3DDC97]" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-geist font-semibold text-[15px] tracking-tight text-console-text">KaraokeCore</span>
                <span className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted mt-1">By Nairoke</span>
              </div>
            </div>

            <nav className="hidden md:flex items-center gap-8 text-[13px] font-medium">
              <button onClick={() => navigate('/subscription')} className="text-console-muted hover:text-console-text transition-colors font-geist">
                Pricing
              </button>
            </nav>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate('/show-host')}
                className="h-11 sm:h-9 px-3 sm:px-4 text-[13px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text rounded-[6px] border border-console-line-strong"
              >
                Show Host
              </Button>
              <Button
                onClick={() => navigate('/venue')}
                className="h-11 sm:h-9 px-3 sm:px-4 text-[13px] font-semibold font-geist bg-console-text text-console-bg hover:bg-console-text/90 rounded-[6px]"
              >
                Host Login
              </Button>
            </div>
          </div>
        </header>

        {/* ── Hero ────────────────────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12">
          <div className="flex items-center gap-2.5 mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-console-ok shadow-[0_0_8px_#3DDC97]" />
            <span className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-muted">
              01 / Karaoke Ops · Live
            </span>
          </div>

          <h1 className="font-geist font-semibold text-[clamp(40px,8vw,88px)] leading-[0.95] tracking-[-0.035em] max-w-5xl">
            Run the room.<br />
            <span className="text-console-muted">The night runs itself.</span>
          </h1>

          <p className="font-geist text-[16px] sm:text-[17px] leading-relaxed text-console-muted max-w-xl mt-7">
            Karaoke operations software for venues — singer queue, song library, custom request approvals,
            and a singer-facing display. Real-time across every device in the room.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button
              onClick={() => navigate('/subscription')}
              className="h-12 px-5 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px] gap-2.5"
            >
              View plans & pricing
              <span className="font-geist-mono text-[11px] opacity-70">→</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/demo')}
              className="h-12 px-5 text-[14px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
            >
              <Play className="h-4 w-4 mr-2" />
              Try the demo
            </Button>
          </div>
        </section>

        {/* ── Features ────────────────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20">
          <SectionMarker n="02 /" title="What's on the console" right="04 features" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FeatureCard
              tag="QUEUE"
              title="Live queue, fully in your hands"
              body="Drag to reorder, skip no-shows, mark sung. Singers see their position update on the room screen the moment you change it."
            />
            <FeatureCard
              tag="LIBRARY"
              title="Your song catalogue, organised"
              body="Bulk-upload via CSV, search by title or artist, toggle individual songs on or off. Custom requests sit in their own approval queue."
            />
            <FeatureCard
              tag="DISPLAY"
              title="Singer page & room screen"
              body="A QR code on the table opens the singer page on any phone. A separate display URL drives the projector with the current queue."
            />
            <FeatureCard
              tag="HOSTS"
              title="Hand off the night, keep the keys"
              body="Enterprise venues create a Show Host account for the person running the night — they can manage the queue and announcements, nothing else."
            />
          </div>
        </section>

        {/* ── Why venues run on it ────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20">
          <SectionMarker n="03 /" title="Why venues run on KaraokeCore" right="03 reasons" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ReasonCard
              role="admin"
              title="Lightning-fast setup"
              body="Upload your existing CSV and the queue is ready in minutes. No installation, no licence dongles, nothing to maintain."
            />
            <ReasonCard
              role="host"
              title="Real-time everything"
              body="Every queue change, request, and approval propagates across host, singer, and projector screens in under a second."
            />
            <ReasonCard
              role="singer"
              title="Singers join in seconds"
              body="No app to download. Scan a QR or open a venue URL — they're in the queue on whatever phone they have."
            />
          </div>
        </section>

        {/* ── Contact CTA ─────────────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-24">
          <div className="bg-console-surface border border-console-line rounded-[10px] p-8 sm:p-12 lg:p-16">
            <div className="max-w-3xl">
              <span className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-signal">
                04 / Talk to us
              </span>
              <h2 className="font-geist font-semibold text-[clamp(28px,5vw,48px)] leading-[1.05] tracking-[-0.025em] mt-4">
                Ready to set up your venue?
              </h2>
              <p className="font-geist text-[16px] text-console-muted mt-5">
                We'll quote pricing, help with your song catalogue, and get the venue on air.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button
                  onClick={() => window.location.href = 'mailto:tubosun@nairoke.com?subject=KaraokeCore Inquiry'}
                  className="h-12 px-5 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email us
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => window.location.href = 'tel:+2348109385214'}
                  className="h-12 px-5 text-[14px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  +234 810 938 5214
                </Button>
              </div>
              <p className="font-geist-mono text-[10px] tracking-[0.12em] uppercase text-console-dim mt-7">
                No commitment · Free consultation
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

function SectionMarker({ n, title, right }: { n: string; title: string; right?: string }) {
  return (
    <div className="flex items-center justify-between mb-7 pb-7 border-b border-console-line">
      <div className="flex items-baseline gap-3.5">
        <span className="font-geist-mono text-[11px] tracking-[0.14em] text-console-signal">{n}</span>
        <span className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-muted">{title}</span>
      </div>
      {right && (
        <span className="font-geist-mono text-[11px] tracking-[0.12em] uppercase text-console-dim hidden sm:inline">{right}</span>
      )}
    </div>
  );
}

function FeatureCard({ tag, title, body }: { tag: string; title: string; body: string }) {
  return (
    <div className="bg-console-surface border border-console-line rounded-[10px] p-6 sm:p-7">
      <span className="inline-block font-geist-mono text-[10px] tracking-[0.16em] text-console-signal px-1.5 py-1 border border-console-signal/40 rounded-[3px]">
        {tag}
      </span>
      <h3 className="font-geist font-medium text-[20px] sm:text-[22px] leading-tight tracking-[-0.02em] mt-10 sm:mt-12 mb-2.5">
        {title}
      </h3>
      <p className="font-geist text-[14px] leading-relaxed text-console-muted max-w-md">
        {body}
      </p>
    </div>
  );
}

function ReasonCard({ role, title, body }: { role: 'admin' | 'host' | 'singer'; title: string; body: string }) {
  const dotColor =
    role === 'admin' ? 'bg-console-primary shadow-[0_0_8px_#4D7CFE]'
    : role === 'host' ? 'bg-console-signal shadow-[0_0_8px_#F5B53D]'
    : 'bg-console-ok shadow-[0_0_8px_#3DDC97]';
  return (
    <div className="bg-console-surface border border-console-line rounded-[10px] p-6 sm:p-7">
      <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
      <h3 className="font-geist font-medium text-[18px] leading-tight tracking-[-0.015em] mt-6 mb-2">
        {title}
      </h3>
      <p className="font-geist text-[13px] leading-relaxed text-console-muted">
        {body}
      </p>
    </div>
  );
}

export default Index;
